/**
 * Shape and wording of the `POST|GET /refresh_portfolio` ("Sync IBKR") response, shared by the three
 * Sync buttons (Portfolio, Signals, Fixed Income).
 *
 * M3 Step 1 (CIO-approved 2026-09-25): a dashboard Sync refreshes HOLDINGS ONLY. It no longer writes
 * the official daily NAV (dbo.PortfolioNAV); the scheduled jobs do (06:15 refresh, 06:45 / 13:45 MT
 * preflight) and the 14:05 MT close mark is final. `syncOk` still decides success. `navOk === false`
 * means the broker NAV couldn't be read on this sync -- an amber note, never a failed sync.
 */
export interface RefreshPortfolioResponse {
  trace?: string;
  syncOk?: boolean;
  synced?: number | null;
  synced_positions?: number | null;
  sync?: { error?: string | null; [k: string]: unknown } | null;
  errors?: string[];
  /** M3 Step 1: whether the broker NAV read succeeded (null when the sync never reached it). */
  navOk?: boolean | null;
  /** M3 Step 1: whether THIS request wrote the NAV of record (always false for a dashboard Sync). */
  navWritten?: boolean;
  /** "holdings_only" for a dashboard Sync; "scheduled" / "refused_final" only on the timer path. */
  navWriteMode?: "holdings_only" | "scheduled" | "refused_final" | string;
  navError?: string | null;
}

export const SYNC_SUCCESS_NAV_NOTE = "The official daily NAV is written by the scheduled jobs.";

export const NAV_UNREAD_NOTE = "Broker NAV couldn't be read; the stored daily NAV is unchanged.";

/** Success line for a Sync: holdings refreshed, and who writes the official NAV. */
export function syncSuccessMessage(synced?: number | null): string {
  const head =
    typeof synced === "number" && Number.isFinite(synced)
      ? `Synced ${synced} position${synced === 1 ? "" : "s"} from IBKR.`
      : "Holdings refreshed from IBKR.";
  return `${head} ${SYNC_SUCCESS_NAV_NOTE}`;
}

/** The amber note when the broker NAV couldn't be read, else null. Never affects syncOk. */
export function navUnreadNote(body: RefreshPortfolioResponse | null | undefined): string | null {
  return body?.navOk === false ? NAV_UNREAD_NOTE : null;
}

/** True when the response reports a failed sync (the same test all three Sync buttons use). */
export function syncReportedFailure(body: RefreshPortfolioResponse | null | undefined): boolean {
  return body?.syncOk === false || Boolean(body?.sync?.error);
}

/** The amber note, but only for a SUCCESSFUL sync: a failed sync shows its red message alone. */
export function successNavNote(body: RefreshPortfolioResponse | null | undefined): string | null {
  return syncReportedFailure(body) ? null : navUnreadNote(body);
}
